import * as Toast from '@radix-ui/react-toast'
import React, { createContext, useCallback, useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { tv } from 'tailwind-variants'
import { ClientOnly } from '~/design-system/components/clientOnly'
import { generateUUIDv4 } from '~/design-system/helpers/misc'
import { Close, Info, OfflinePin, Warning } from '~/icons'

type ToastType = 'success' | 'error' | 'info'

interface ToastMessage {
  id: string
  message: string
  type: ToastType
}

interface ShowToastProps {
  message: string
  type: ToastType
  removeAfter?: number
}

const toastVariants = tv({
  base: 'w-80  flex gap-2 p-2 pl-4 text-white bg-gray-100 rounded-md items-center self-center data-[state=open]:animate-slideIn data-[state=closed]:animate-hide data-[swipe=cancel]:translate-y-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=end]:animate-swipeOut',
  variants: {
    status: {
      success: 'bg-primary-success',
      error: 'bg-primary-error',
      info: 'bg-primary-info',
    },
  },
})

const iconsChange = {
  success: <OfflinePin className="text-xl" />,
  error: <Warning className="text-xl fill-white" />,
  info: <Info className="text-xl" />,
}

const ToastContext = createContext<{
  show: (toast: ShowToastProps) => void
  remove: (id: string) => void
} | null>(null)

/*
 *  Remove o Toast após 3 segundos (3000 ms) por padrão
 *  Se não for passado nenhum  para o removeAfter no metodo show
 */
const REMOVE_AFTER_DEFAULT = 3000

export const GlobalToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const show = useCallback(({ message, type, removeAfter = REMOVE_AFTER_DEFAULT }: ShowToastProps) => {
    const id = generateUUIDv4()
    setToasts((prevToasts) => [...prevToasts, { id, message, type }])

    setTimeout(() => {
      remove(id)
    }, removeAfter)
    return id
  }, [])

  const remove = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id))
  }, [])

  const toastPortal = (
    <div className="z-50 relative">
      <Toast.Provider swipeDirection="right">
        {toasts.map((toast) => (
          <Toast.Root
            className={toastVariants({ status: toast.type })}
            duration={REMOVE_AFTER_DEFAULT}
            key={toast.id}
            open={true}
          >
            {iconsChange[toast.type]}

            <Toast.Title>{toast.message}</Toast.Title>

            <div className="border-l px-3 border-white" onClick={() => remove(toast.id)}>
              <Toast.Close aria-label="Close" asChild>
                <span aria-label="Fechar" className="text-xl cursor-pointer" aria-hidden>
                  <Close className="fill-white" />
                </span>
              </Toast.Close>
            </div>
          </Toast.Root>
        ))}
        <Toast.Viewport className="fixed top-0 right-0 flex flex-col p-6 gap-2.5 w-screen m-0 list-none z-50 outline-none justify-center" />
      </Toast.Provider>
    </div>
  )

  return (
    <ToastContext.Provider value={{ show, remove }}>
      {children}
      {/*  Ref: https://github.com/remix-run/remix/discussions/7874*/}
      <ClientOnly>{() => createPortal(toastPortal, document.body)}</ClientOnly>
    </ToastContext.Provider>
  )
}

/*
 * Hook para usar o Toast
 * */
export const useGlobalToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useGlobalToast must be used within a GlobalToastProvider')
  }
  return context
}
